import * as R from 'ramda';
import * as React from 'react';
import { useState } from 'react';
import { bem } from 'css-util';
import './PainRatingForm.scss';

interface Props {
  onSelect: (rating: number) => void
  onCancel: () => void
}

const CSS = bem('pain-rating');

const SCALE_BANDS: [string, string][] = [
  ['0', 'No pain'],
  ['1–3', 'Mild pain'],
  ['4–6', 'Moderate pain'],
  ['7–8', 'Strong pain'],
  ['9–10', 'Very severe / worst possible pain']
];

const SCALE_DEFINITIONS: [number, string][] = [
  [0, 'No pain.'],
  [1, 'Pain is very mild, barely noticeable. Most of the time you don’t think about it.'],
  [2, 'Minor pain. It’s annoying. You may have sharp pain now and then.'],
  [3, 'Noticeable pain. It may distract you, but you can get used to it.'],
  [4, 'Moderate pain. If you are involved in an activity, you’re able to ignore the pain for ' +
     'a while. But it is still distracting.'],
  [5, 'Moderately strong pain. You can’t ignore it for more than a few minutes. But with ' +
     'effort you can still work or do some social activities.'],
  [6, 'Moderately stronger pain. You avoid some of your normal daily activities. You have ' +
     'trouble concentrating.'],
  [7, 'Strong pain. It keeps you from doing normal activities.'],
  [8, 'Very strong pain. It’s hard to do anything at all.'],
  [9, 'Pain that is very hard to bear. You can’t carry on a conversation.'],
  [10, 'Worst pain possible.']
];

function PainRatingForm({ onSelect, onCancel }: Props) {
  const [openedScale, setOpenedScale] = useState(false);

  return (
    <div className={CSS('form')()}>
      <p className={CSS('prompt')()}>
        How would you rate your pain right now?
      </p>
      <dl className={CSS('legend')()}>
        {SCALE_BANDS.map(([range, label]) =>
          <div key={range} className={CSS('band')()}>
            <dt className={CSS('band-range')()}>{range}</dt>
            <dd className={CSS('band-label')()}>{label}</dd>
          </div>
        )}
      </dl>
      <div className={CSS('options')()}>
        {R.range(0, 11).map(rating =>
          <button
            key={rating}
            className={CSS('option')()}
            onClick={() => onSelect(rating)}
          >
            {rating}
          </button>
        )}
      </div>
      <ScaleInfo opened={openedScale} onToggle={() => setOpenedScale(opened => !opened)} />
      <button className={CSS('cancel')()} onClick={onCancel}>
        cancel
      </button>
    </div>
  );
}

interface ScaleInfoProps {
  opened: boolean
  onToggle: () => void
}

function ScaleInfo({ opened, onToggle }: ScaleInfoProps) {
  return (
    <div className={CSS('info')()}>
      <button className={CSS('toggle')()} onClick={onToggle} aria-expanded={opened}>
        {opened ? '▾' : '▸'} What do these numbers mean?
      </button>
      {opened &&
        <dl className={CSS('definitions')()}>
          {SCALE_DEFINITIONS.map(([rating, definition]) =>
            <div key={rating} className={CSS('definition')()}>
              <dt className={CSS('definition-num')()}>{rating}</dt>
              <dd className={CSS('definition-text')()}>{definition}</dd>
            </div>
          )}
        </dl>
      }
    </div>
  );
}

export default PainRatingForm;
